import { useState } from 'react';
import { apiFetch } from '../../api/client';
import { useAdminRoleApplications, RoleApplication } from '../../api/hooks';

const ROLE_LABELS: Record<string, string> = { issuer: 'Credential Issuer', verifier: 'Credential Verifier' };

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colors[status] ?? 'bg-slate-100 text-slate-700'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ApplicationRow({ app, onAction }: { app: RoleApplication; onAction: () => void }) {
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/admin/role-applications/${app.id}/approve`, { method: 'PUT', body: '{}' });
      onAction();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) { setError('Reason is required'); return; }
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/admin/role-applications/${app.id}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ reason }),
      });
      onAction();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-white p-5 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-text-main">
            {ROLE_LABELS[app.requestedRole] ?? app.requestedRole}
          </p>
          <p className="text-xs text-text-faint mt-0.5">
            {app.orgName} · {app.orgType}
            {app.website && <> · <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{app.website}</a></>}
          </p>
          <p className="text-[11px] text-text-faint mt-1">User ID: {app.userId}</p>
        </div>
        <StatusBadge status={app.status} />
      </div>

      {app.purpose && (
        <p className="text-sm text-text-secondary">{app.purpose}</p>
      )}

      {app.rejectionReason && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <span className="font-semibold">Rejection reason: </span>{app.rejectionReason}
        </div>
      )}

      <p className="text-[11px] text-text-faint">
        Submitted {new Date(app.createdAt).toLocaleDateString()}
        {app.reviewedAt && ` · Reviewed ${new Date(app.reviewedAt).toLocaleDateString()}`}
      </p>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {app.status === 'pending' && !rejectMode && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={approve}
            disabled={loading}
            className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50"
          >
            {loading ? '…' : 'Approve'}
          </button>
          <button
            onClick={() => setRejectMode(true)}
            disabled={loading}
            className="rounded-lg border border-red-300 bg-red-50 px-4 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      )}

      {app.status === 'pending' && rejectMode && (
        <div className="space-y-2">
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="input w-full resize-none text-sm"
            placeholder="Reason for rejection…"
          />
          <div className="flex gap-2">
            <button
              onClick={reject}
              disabled={loading}
              className="rounded-lg border border-red-300 bg-red-50 px-4 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {loading ? '…' : 'Confirm Reject'}
            </button>
            <button
              onClick={() => { setRejectMode(false); setReason(''); setError(null); }}
              className="text-sm text-text-faint hover:text-text-main px-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminRoleApplicationsPage() {
  // Approved and Rejected give the review history its own tabs. The API only
  // distinguishes pending from all, so those two filter the full list here.
  // Previously a rejection dropped out of the default Pending view at once and
  // was only findable under All.
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const { data: fetched, loading, error, refetch } = useAdminRoleApplications(filter === 'pending' ? 'pending' : undefined);
  const applications =
    filter === 'approved' || filter === 'rejected'
      ? (fetched ?? []).filter((a) => a.status === filter)
      : fetched;

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-8 px-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-main">Role Applications</h1>
          <p className="mt-1 text-sm text-text-secondary">Review and approve role upgrade requests from holders.</p>
        </div>
        <div className="flex rounded-lg border border-border-subtle overflow-hidden text-sm">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 font-medium capitalize ${filter === f ? 'bg-indigo-600 text-white' : 'bg-white text-text-secondary hover:bg-slate-50'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="size-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && !error && applications?.length === 0 && (
        <div className="rounded-xl border border-border-subtle bg-slate-50 py-16 text-center">
          <span className="material-symbols-outlined text-4xl text-text-faint">inbox</span>
          <p className="mt-2 text-sm text-text-secondary">
            {filter === 'all' ? 'No applications yet.' : `No ${filter} applications.`}
          </p>
        </div>
      )}
      <div className="space-y-4">
        {applications?.map((app) => (
          <ApplicationRow key={app.id} app={app} onAction={refetch} />
        ))}
      </div>
    </div>
  );
}
